import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import DashboardShell from "@/components/DashboardShell";
import { Link2, Users, LayoutDashboard, PlayCircle, Mail, FileBadge } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/sonner";

const LINKS = [
  { to: "/me", label: "Dashboard", icon: LayoutDashboard, testId: "nav-me-dashboard" },
  { to: "/me/students", label: "My Students", icon: Users, testId: "nav-me-students" },
  { to: "/me/link", label: "My Link", icon: Link2, testId: "nav-me-link" },
  { to: "/me/inbox", label: "Inbox", icon: Mail, testId: "nav-me-inbox" },
  { to: "/me/sample-report", label: "Sample Report", icon: FileBadge, testId: "nav-me-sample-report" },
  { to: "/me/demo", label: "Launch Demo", icon: PlayCircle, testId: "nav-me-demo" },
];

export default function MarketingStudents() {
  const navigate = useNavigate();
  const [students, setStudents] = useState([]);

  const load = () => api.get("/marketing/students").then((res) => setStudents(res.data));
  useEffect(() => { load(); }, []);

  const activate = async (studentId) => {
    try {
      await api.post(`/marketing/students/${studentId}/activate`);
      toast.success("License activated");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not activate");
    }
  };

  return (
    <DashboardShell links={LINKS} activePath="/me/students">
      <h1 className="font-heading text-2xl font-bold text-slate-900 mb-6" data-testid="marketing-students-heading">My Students</h1>
      <div className="rounded-xl border border-slate-200 bg-white overflow-hidden">
        <Table data-testid="marketing-students-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Phone</TableHead>
              <TableHead>Verification</TableHead><TableHead>License</TableHead><TableHead>Assessment</TableHead>
              <TableHead>Progress</TableHead><TableHead>Report</TableHead><TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {students.map((s) => (
              <TableRow key={s.student_id} data-testid={`marketing-student-row-${s.student_id}`}>
                <TableCell>{s.name}</TableCell>
                <TableCell>{s.email}</TableCell>
                <TableCell>{s.phone}</TableCell>
                <TableCell><Badge variant={s.verification_status === "Verified" ? "default" : "secondary"}>{s.verification_status}</Badge></TableCell>
                <TableCell>
                  {s.license_blocked ? (
                    <span className="text-xs font-semibold text-rose-600" data-testid={`me-license-blocked-flag-${s.student_id}`}>Blocked — contact admin to top up licenses</span>
                  ) : s.license_status}
                </TableCell>
                <TableCell>{s.assessment_status}</TableCell>
                <TableCell>{s.progress}%</TableCell>
                <TableCell>{s.report_status}</TableCell>
                <TableCell>
                  {s.license_status === "NOT_ISSUED" && (
                    <Button size="sm" data-testid={`activate-license-button-${s.student_id}`} onClick={() => activate(s.student_id)} className="bg-indigo-600 hover:bg-indigo-700 rounded-full">
                      Activate License
                    </Button>
                  )}
                  {s.report_status === "READY" && (
                    <Button size="sm" variant="outline" data-testid={`view-report-button-${s.student_id}`} onClick={() => navigate(`/me/students/${s.student_id}/report`)}>
                      View Report
                    </Button>
                  )}
                  {s.aptitude_report_status === "READY" && (
                    <Button size="sm" variant="outline" data-testid={`view-aptitude-report-button-${s.student_id}`} onClick={() => navigate(`/me/students/${s.student_id}/aptitude-report`)}>
                      Aptitude Report
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {students.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-slate-400 py-10">No attributed students yet</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>
    </DashboardShell>
  );
}
